import type { ReactNode } from "react";
import type { TenantRole } from "@fts/core";
import { useT } from "../i18n";
import { STATUS_TONE, type VehicleStatus } from "../lib/fleet";
import { Icon, type IconName } from "./Icon";

export function PageHeader({ title, subtitle, eyebrow, actions }: { title: ReactNode; subtitle?: ReactNode; eyebrow?: ReactNode; actions?: ReactNode }) {
  return (
    <div className="page-header">
      <div>
        {eyebrow && <div className="page-eyebrow">{eyebrow}</div>}
        <h1 className="page-title">{title}</h1>
        {subtitle && <p className="page-subtitle">{subtitle}</p>}
      </div>
      {actions && <div className="page-actions">{actions}</div>}
    </div>
  );
}

export function Panel({ title, meta, children, flush }: { title?: ReactNode; meta?: ReactNode; children: ReactNode; flush?: boolean }) {
  return (
    <section className={`panel${flush ? " panel-flush" : ""}`}>
      {(title || meta) && (
        <header className="panel-header">
          {title && <h2 className="panel-title">{title}</h2>}
          {meta && <div className="panel-meta">{meta}</div>}
        </header>
      )}
      {children}
    </section>
  );
}

export function StatusBadge({ status }: { status: VehicleStatus }) {
  const t = useT();
  return (
    <span className={`badge tone-${STATUS_TONE[status]}`}>
      <span className={`status-dot tone-${status}`} aria-hidden="true" />
      {t(`status.${status}`)}
    </span>
  );
}

const ROLE_TONE: Record<TenantRole, string> = { admin: "accent", fleet_manager: "info", safety: "warn", viewer: "neutral" };

export function RoleBadge({ role }: { role: TenantRole }) {
  const t = useT();
  return <span className={`badge tone-${ROLE_TONE[role]}`}>{t(`role.${role}`)}</span>;
}

export function EmptyState({ icon = "info", title, children, action }: { icon?: IconName; title?: ReactNode; children?: ReactNode; action?: ReactNode }) {
  return (
    <div className="empty">
      <span className="empty-icon">
        <Icon name={icon} size={22} />
      </span>
      {title && <h2 className="empty-title">{title}</h2>}
      {children && <p className="empty-body">{children}</p>}
      {action}
    </div>
  );
}

export function Note({ children }: { children: ReactNode }) {
  return (
    <p className="note">
      <Icon name="info" size={16} />
      <span>{children}</span>
    </p>
  );
}

export function Mono({ children }: { children: ReactNode }) {
  return <span className="mono">{children}</span>;
}
